import React, { useEffect, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { DialogShell } from './ui/DialogShell';
import { EditarAporteModal } from './EditarAporteModal';
import { Movimiento } from '../lib/supabase';
import { movimientoService } from '../services/movimientoService';
import { AporteAnalysis } from '../services/aporteService';
import { formatCurrency, parseDateOnly } from '../utils/formatters';
import { TextInput } from './ui/TextInput';
import { Checkbox } from './ui/Checkbox';
import { reportarErrorInesperado } from '../utils/reportarErrorInesperado';
import { FormErrorAlert } from './ui/FormErrorAlert';

interface AporteMiembroDrawerProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly miembro: AporteAnalysis;
  readonly lumisialId: string;
  /** Editar y anular aportes: solo quien opera el dinero de este Lumisial. */
  readonly puedeOperar: boolean;
  readonly onAporteModificado: () => void;
}

function formatFechaAporte(fecha: string): string {
  const date = parseDateOnly(fecha);
  return date.toLocaleDateString('es-CL', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatMonto(monto: number): string {
  return formatCurrency(monto);
}

interface AnulacionDialogProps {
  readonly aporte: Movimiento;
  readonly onClose: () => void;
  readonly onConfirm: (motivo: string) => Promise<void>;
  readonly error: string | null;
  readonly procesando: boolean;
}

const AnulacionDialog: React.FC<AnulacionDialogProps> = ({
  aporte,
  onClose,
  onConfirm,
  error,
  procesando,
}) => {
  const [isChecked, setIsChecked] = useState(false);
  const [motivo, setMotivo] = useState('');

  const categoriasLabel: Record<string, string> = {
    aporte_mensual: 'Aporte Lumisial',
    cuota_iglesia: 'Cuota Iglesia',
  };
  const categoria = aporte.categoria_aporte ? (categoriasLabel[aporte.categoria_aporte] ?? aporte.categoria_aporte) : '';

  const descripcion = `${formatFechaAporte(aporte.fecha)} · ${categoria} · ${formatMonto(aporte.monto)}`;

  const handleConfirm = async () => {
    if (!isChecked || procesando) return;
    await onConfirm(motivo);
  };

  // Se apila como modal anidado sobre el modal de aportes del miembro (level="nested").
  return (
    <DialogShell
      isOpen
      onClose={onClose}
      size="sm"
      level="nested"
      showCloseButton={false}
      header={(
        <div className="sticky top-0 z-10 bg-red-600 text-white p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500 rounded-lg">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-bold">Anular aporte</h3>
          </div>
          <button type="button" onClick={onClose} disabled={procesando} className="text-white/80 hover:text-white transition-colors disabled:opacity-50" aria-label="Cerrar">
            <X className="w-6 h-6" />
          </button>
        </div>
      )}
    >
      <div className="space-y-4">
        {/* Descripción del aporte */}
        <p className="text-slate-700">{descripcion}</p>

        {/* Consecuencias */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 font-semibold mb-2 text-sm uppercase tracking-wide">
            ⚠️ Consecuencias de esta acción:
          </p>
          <ul className="space-y-2">
            {[
              'El aporte quedará marcado como anulado',
              'Los totales del miembro se actualizarán',
            ].map((item) => (
              <li key={item} className="flex items-start gap-2 text-red-700 text-sm">
                <span className="text-red-500 mt-0.5">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <TextInput
          label="Motivo de anulación (opcional)"
          value={motivo}
          onChange={setMotivo}
          placeholder="Ej: registro duplicado, error de monto..."
        />

        <FormErrorAlert error={error} />

        <div className="p-3 bg-slate-100 rounded-lg">
          <Checkbox
            label="Entiendo que esta acción no se puede deshacer"
            checked={isChecked}
            onChange={setIsChecked}
          />
        </div>

        {/* Botones */}
        <div className="flex gap-3 justify-end pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={procesando}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!isChecked || procesando}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
              isChecked && !procesando
                ? 'bg-red-600 hover:bg-red-700 cursor-pointer'
                : 'bg-red-300 cursor-not-allowed'
            }`}
          >
            {procesando ? 'Anulando...' : 'Confirmar anulación'}
          </button>
        </div>
      </div>
    </DialogShell>
  );
};

export const AporteMiembroDrawer: React.FC<AporteMiembroDrawerProps> = ({
  isOpen,
  onClose,
  miembro,
  lumisialId,
  puedeOperar,
  onAporteModificado,
}) => {
  const [aportes, setAportes] = useState<Movimiento[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Edición
  const [movimientoEditando, setMovimientoEditando] = useState<Movimiento | null>(null);

  // Anulación
  const [movimientoAnulando, setMovimientoAnulando] = useState<Movimiento | null>(null);
  const [anulandoId, setAnulandoId] = useState<string | null>(null);
  const [errorAnulacion, setErrorAnulacion] = useState<string | null>(null);

  const cargarAportes = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await movimientoService.getByMiembro(lumisialId, miembro.miembro_id);
      setAportes(data);
    } catch (e: unknown) {
      const msg = 'No pudimos cargar los aportes del miembro.';
      setError(msg);
      reportarErrorInesperado(e, msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen || !miembro.miembro_id) return;
    void cargarAportes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, miembro.miembro_id]);

  const handleAporteModificado = () => {
    void cargarAportes();
    onAporteModificado();
  };

  const handleAnular = async (motivo: string) => {
    if (!movimientoAnulando) return;
    setAnulandoId(movimientoAnulando.id);
    setErrorAnulacion(null);
    try {
      await movimientoService.anular(movimientoAnulando.id, motivo || undefined);
      setMovimientoAnulando(null);
      setAnulandoId(null);
      handleAporteModificado();
    } catch (e: unknown) {
      const msg = 'No pudimos anular el aporte.';
      setErrorAnulacion(msg);
      setAnulandoId(null);
      reportarErrorInesperado(e, msg);
    }
  };

  return (
    <>
      <DialogShell
        isOpen={isOpen}
        onClose={onClose}
        title={`Aportes de ${miembro.nombres} ${miembro.apellidos}`}
        size="lg"
      >
        <div>
          {/* Estado: cargando */}
          {loading && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          )}

          {/* Estado: error */}
          {!loading && error && (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <p className="text-red-600 text-sm">{error}</p>
              <button
                type="button"
                onClick={() => void cargarAportes()}
                className="px-4 py-2 text-sm font-medium bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Reintentar
              </button>
            </div>
          )}

          {/* Estado: vacío */}
          {!loading && !error && aportes.length === 0 && (
            <div className="flex justify-center items-center py-12">
              <p className="text-slate-500 text-sm">Este miembro no tiene aportes registrados.</p>
            </div>
          )}

          {/* Tabla */}
          {!loading && !error && aportes.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                    <th className="pb-2 pr-3">Fecha</th>
                    <th className="pb-2 pr-3">Categoría</th>
                    <th className="pb-2 pr-3">Monto</th>
                    <th className="pb-2 pr-3">Descripción</th>
                    <th className="pb-2">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {aportes.map((aporte) => (
                    <tr
                      key={aporte.id}
                      className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                    >
                      <td className="py-2 pr-3 whitespace-nowrap text-slate-700">
                        {formatFechaAporte(aporte.fecha)}
                      </td>
                      <td className="py-2 pr-3">
                        {aporte.categoria_aporte === 'aporte_mensual' && (
                          <span className="inline-flex items-center justify-center text-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Aporte Lumisial
                          </span>
                        )}
                        {aporte.categoria_aporte === 'cuota_iglesia' && (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                            Cuota Iglesia
                          </span>
                        )}
                        {!aporte.categoria_aporte && (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 whitespace-nowrap font-medium text-slate-800">
                        {formatMonto(aporte.monto)}
                      </td>
                      <td className="py-2 pr-3 text-slate-500 max-w-[160px] truncate">
                        {aporte.descripcion ?? '—'}
                      </td>
                      <td className="py-2">
                        {puedeOperar && (
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => setMovimientoEditando(aporte)}
                            className="px-2 py-1 text-xs rounded border border-blue-300 text-blue-700 hover:bg-blue-50 transition-colors"
                          >
                            ✏️ Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setErrorAnulacion(null);
                              setMovimientoAnulando(aporte);
                            }}
                            className="px-2 py-1 text-xs rounded border border-red-300 text-red-700 hover:bg-red-50 transition-colors"
                          >
                            🚫 Anular
                          </button>
                        </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </DialogShell>

      {/* Modal de edición (anidado sobre el modal de aportes) */}
      {movimientoEditando && (
        <EditarAporteModal
          isOpen={!!movimientoEditando}
          onClose={() => setMovimientoEditando(null)}
          onSuccess={handleAporteModificado}
          movimiento={movimientoEditando}
          level="nested"
        />
      )}

      {/* Dialog inline de anulación */}
      {movimientoAnulando && (
        <AnulacionDialog
          aporte={movimientoAnulando}
          onClose={() => {
            setMovimientoAnulando(null);
            setErrorAnulacion(null);
          }}
          onConfirm={handleAnular}
          error={errorAnulacion}
          procesando={anulandoId !== null}
        />
      )}
    </>
  );
};
